import React from 'react';
import { Helmet } from 'react-helmet';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Assembly</title>
        <meta name="description" content="Assembly's Terms of Service and conditions for using our employee recognition platform." />
      </Helmet>
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <p className="text-sm text-gray-600 mb-0">
                  <strong>Last updated:</strong> July 30, 2019
                </p>
              </div>

              <div className="mb-8">
                <p className="text-gray-700 mb-6">
                  Welcome to the Assembly website (the "Site"). CarrotHR, Inc. (d/b/a "Assembly") provides its services to you subject to the following conditions. Before you may use the Site, you must read and accept all of the terms and conditions in, and linked to, this Terms of Service ("TOS") and the Privacy Policy.
                </p>
                <p className="text-gray-700 mb-6">
                  This TOS is effective upon acceptance. The use of any functionality of the Site constitutes acceptance of this TOS. If this TOS conflicts with any other documents, the TOS will control for the purposes of usage of the Site. You may only use certain portions of the Site if you are an Assembly registered user. If you do not agree to be bound by this TOS and the Privacy Policy, you may not use the Site in any way.
                </p>
              </div>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Description of Service</h2>
                <p className="text-gray-700">
                  Assembly provides registered organizations a peer to peer recognition system (the "Service"). You are responsible for obtaining access to the Site, and that access may involve third-party fees (such as Internet service provider or airtime charges). In addition, you must provide and are responsible for all equipment necessary to access the Site.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. License and Site Access</h2>
                <p className="text-gray-700 mb-4">
                  Assembly grants you a limited license to access and make use of the Site and the Service. This license does not include:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Any downloading or copying of account information for the benefit of another vendor or any other third party</li>
                  <li>Caching, unauthorized hypertext links to the Site and the framing of any Content available through the Site</li>
                  <li>Uploading, posting, or transmitting any content that you do not have a right to make available</li>
                  <li>Uploading, posting, or transmitting any material that contains software viruses or malicious code</li>
                  <li>Any action that imposes an unreasonable load on Assembly's infrastructure</li>
                  <li>Any use of data mining, robots, or similar data gathering and extraction tools</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  Any unauthorized use by you shall terminate the permission or license granted to you or your organization by Assembly.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Eligibility</h2>
                <p className="text-gray-700 mb-4">
                  The Assembly service is not available to any users suspended or removed from the system by Assembly for any reason. If you or your organization do not qualify, you may not use the Assembly Service or the Site.
                </p>
                <p className="text-gray-700">
                  In consideration of your use of the Site, you represent that you legally belong to the organization and are not a person barred from receiving services under the laws of the United States or other applicable jurisdiction. You also agree to provide true, accurate, current and complete information about yourself as prompted by the Site's registration form.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Submitted Content</h2>
                <p className="text-gray-700 mb-4">
                  For purposes of this Agreement, the term "Content" means any work of authorship and includes, without limitation, video, audio, photographs, images, illustrations, animations, logos, tools, written posts, replies, and comments, information, data, text, software, scripts, executable files, graphics, and interactive features.
                </p>
                <p className="text-gray-700 mb-4">
                  As between you and Assembly, you retain ownership of all intellectual property rights to your Content, and Assembly retains ownership of all intellectual property rights to its Service and all Content made available through the Service other than your Content.
                </p>
                <p className="text-gray-700">
                  As a Subscriber of the Service, you grant to Assembly a worldwide, non-exclusive, royalty-free, fully-paid, irrevocable (other than a termination of this Agreement), transferable right and license to download, copy, store, view, display, perform, and analyze the Content uploaded and/or posted to Assembly for purposes of operating and providing the Service and Assembly's internal business purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Limitation of Liability</h2>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <p className="text-gray-700 font-medium">
                    YOU EXPRESSLY UNDERSTAND AND AGREE THAT ASSEMBLY AND ITS SUBSIDIARIES, AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, PARTNERS AND LICENSORS SHALL NOT BE LIABLE TO YOU FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES, INCLUDING, BUT NOT LIMITED TO, DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA OR OTHER INTANGIBLE LOSSES.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Indemnity</h2>
                <p className="text-gray-700">
                  You agree to indemnify and hold Assembly (and its officers, directors, agents, subsidiaries, joint ventures, and employees) harmless from any claim or demand, including reasonable attorneys' fees, arising out of or related to your breach of this TOS, or your violation of any law or the rights of a third party.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Electronic Communication</h2>
                <p className="text-gray-700">
                  When you use the Site or send emails to Assembly, you are communicating with Assembly electronically. You consent to receive communications electronically from Assembly. Assembly will communicate with you by email or by posting notices on the Site. You agree that all agreements, notices, disclosures and other communications that are provided to you electronically satisfy any legal requirement that such communications be in writing.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Third-Party Links</h2>
                <p className="text-gray-700">
                  The Site or third parties may provide links to other World Wide Web sites or resources. Because Assembly has no control over such sites and resources, you acknowledge and agree that Assembly is not responsible for the availability of such external sites or resources, and does not endorse and is not responsible or liable for any content, advertising, products or other materials on or available from such sites or resources.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Trademarks and Copyright</h2>
                <p className="text-gray-700 mb-4">
                  The trademarks, logos and service marks displayed on the Site are the property of Assembly and other parties. Users are prohibited from using any Marks for any purpose including, but not limited to use as metatags on other pages or sites on the World Wide Web without the written permission of Assembly or such third party which may own the Marks.
                </p>
                <p className="text-gray-700">
                  All information and content including any software programs available on or through the Site is protected by copyright. Users are prohibited from modifying, copying, distributing, transmitting, displaying, publishing, selling, licensing, creating derivative works or using any Content available on or through the Site for commercial or public purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Use of Google Services</h2>
                <p className="text-gray-700">
                  Assembly has implemented the Invisible reCAPTCHA for security purposes, and the usage of Invisible reCAPTCHA by users is subject to the Google{' '}
                  <a href="https://www.google.com/intl/en/policies/privacy/" target="_blank" rel="noopener noreferrer" className="text-assembly-orange hover:underline">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="https://www.google.com/intl/en/policies/terms/" target="_blank" rel="noopener noreferrer" className="text-assembly-orange hover:underline">
                    Terms of Use
                  </a>.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Modifications and Notifications</h2>
                <p className="text-gray-700">
                  Assembly reserves the right to modify or replace this Agreement at any time and in Assembly's sole discretion. Assembly will indicate at the top of this Agreement the date such document was last updated. Any changes will be effective immediately upon posting the revised version. It is your responsibility to regularly check the Site to determine if there have been changes to this Agreement and to review such changes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Intellectual Property Claims</h2>
                <p className="text-gray-700 mb-4">
                  Assembly respects the intellectual property of others, and we ask our users to do the same. Assembly may, in appropriate circumstances and at its discretion, disable and/or terminate the accounts of users who may be infringing the intellectual property of a third party.
                </p>
                <p className="text-gray-700 mb-4">
                  If you believe that your work has been copied in a way that constitutes copyright infringement, or your intellectual property rights have been otherwise violated, please provide Assembly's Copyright Agent the necessary information including an electronic or physical signature, description of the copyrighted work, location of the infringing material, and your contact information.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Copyright Agent Contact:</strong><br />
                    Email: <a href="mailto:support@joinassembly.com" className="text-assembly-orange hover:underline">support@joinassembly.com</a>
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
                <p className="text-gray-700">
                  This TOS and the relationship between you and Assembly will be governed by the laws of the State of Delaware without regard to its conflict of law provisions. You and Assembly agree to submit to the personal jurisdiction of the federal and state courts located in the State of Delaware with respect to any legal proceedings that may arise in connection with this TOS.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
                <p className="text-gray-700">
                  For questions about these Terms of Service, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700 mb-2">
                    <strong>Email:</strong> <a href="mailto:support@joinassembly.com" className="text-assembly-orange hover:underline">support@joinassembly.com</a>
                  </p>
                  <p className="text-gray-700">
                    <strong>Address:</strong> 548 Market Street, San Francisco, CA 94104
                  </p>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Terms;